import AppContext from 'App/AppContext';
import { IPSeparator, IPSpinner, IPText } from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { PrevNextButtons } from 'components/Buttons/PrevNextButtons';
import PeriodFilter, { IPeriodFilterData } from 'components/Filter/PeriodFilter';
import { getPeriodFilter } from 'components/Filter/PeriodFilterHelper';
import { ProcessingActivityReportContent } from 'components/ProcessingActivity/ProcessingActivityReportContent';
import { TaskEffectivenessOverview } from 'components/Tasks/Dashboard/TaskEffectivenessOverview';
import { TaskTagOverview } from 'components/Tasks/Dashboard/TaskTagOverview';
import ScrollableStackItem from 'components/Utils/ScrollableStackItem';
import { globalSceneBarItemHeight, globalStackTokensGapMedium, globalTextStylesBold } from 'globalStyles';
import ProcessingActivity from 'models/processingActivity';
import { ProcessingActivityContext } from 'models/processingActivityContext';
import { TagSystemTypes } from 'models/tag';
import Task, { TaskStateId, TaskTypes } from 'models/tasks/task';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  apiGetProcessingActivities,
  apiGetProcessingActivityContext,
  apiGetProcessingActivityReport,
} from 'services/Api/processingActivityService';
import { apiGetTasksForProcessingActivity } from 'services/Api/taskService';
import { apiRequest } from 'services/Auth/authConfig';
import { LocalStorageKeys } from 'utils/localstorage';
import { sortOnCode } from 'utils/sorting';

interface IProcessingActivityDashboardProps {
  selectedProcessingActivity?: ProcessingActivity;
  isLoading: boolean;
}

export const TabDashboard = (props: IProcessingActivityDashboardProps) => {
  const { t } = useTranslation(['processingActivity', 'translation']);
  const appContext = useContext(AppContext);
  const history = useHistory();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoadingTasks, setIsLoadingTasks] = useState<boolean>(false);
  const [period, setPeriod] = useState<IPeriodFilterData | undefined>(undefined);
  const [processingActivities, setProcessingActivities] = useState<ProcessingActivity[]>([]);
  const [isLoadingProcessingActivities, setIsLoadingProcessingActivities] = useState<boolean>(false);
  const [report, setReport] = useState<ProcessingActivity | undefined>(undefined);
  const [reportContext, setReportContext] = useState<ProcessingActivityContext | undefined>(undefined);
  const [isLoadingReport, setIsLoadingReport] = useState<boolean>(false);

  useEffect(() => {
    const loadTasks = async () => {
      if (!props.selectedProcessingActivity || !period) return;

      try {
        setIsLoadingTasks(true);
        const range = getPeriodFilter(period, appContext);
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const result = await apiGetTasksForProcessingActivity(
          props.selectedProcessingActivity.processingActivityId,
          range.start,
          range.end,
          accessToken,
          appContext.globalDataCache,
        );
        setTasks(result.tasks ?? []);
      } catch (err) {
        appContext.setError(err);
      } finally {
        setIsLoadingTasks(false);
      }
    };

    void loadTasks();
  }, [appContext, period, props.selectedProcessingActivity]);

  useEffect(() => {
    const loadProcessingActivities = async () => {
      try {
        setIsLoadingProcessingActivities(true);
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const result = await apiGetProcessingActivities(accessToken, appContext.globalDataCache);
        result.sort((activityA, activityB) => sortOnCode(activityA.code, activityB.code));
        setProcessingActivities(result);
      } catch (err) {
        appContext.setError(err);
      } finally {
        setIsLoadingProcessingActivities(false);
      }
    };

    void loadProcessingActivities();
  }, [appContext]);

  useEffect(() => {
    const loadReport = async () => {
      if (!props.selectedProcessingActivity) return;

      try {
        setIsLoadingReport(true);
        setReport(undefined);
        setReportContext(undefined);
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const processingActivityId = props.selectedProcessingActivity.processingActivityId;
        const [reportResult, contextResult] = await Promise.all([
          apiGetProcessingActivityReport(processingActivityId, accessToken, appContext.globalDataCache),
          apiGetProcessingActivityContext(processingActivityId, accessToken, appContext),
        ]);
        setReport(reportResult);
        setReportContext(contextResult);
      } catch (err) {
        appContext.setError(err);
      } finally {
        setIsLoadingReport(false);
      }
    };

    void loadReport();
  }, [appContext, props.selectedProcessingActivity]);

  const selectedProcessingActivityIndex = useMemo(
    () =>
      processingActivities.findIndex(
        (activity) => activity.processingActivityId === props.selectedProcessingActivity?.processingActivityId,
      ),
    [processingActivities, props.selectedProcessingActivity?.processingActivityId],
  );

  const navigateToProcessingActivity = (offset: number) => {
    const processingActivity = processingActivities[selectedProcessingActivityIndex + offset];
    if (!processingActivity) return;

    history.push(`/processingactivity/${processingActivity.processingActivityId}/dashboard`);
  };

  const tagCounts = useMemo(() => {
    const excludedSystemTypes = [
      TagSystemTypes.MonitoringFinding,
      TagSystemTypes.NonConformity,
      TagSystemTypes.Incident,
    ];
    const counts: Record<number, number> = {};

    tasks
      .filter((task) => task.taskType !== TaskTypes.Monitoring)
      .filter((task) => {
        const tags = appContext.globalDataCache.tags.getItemsForId(task.tagIds ?? []);

        return !tags.some((tag) => excludedSystemTypes.includes(tag.tagSystemType as TagSystemTypes));
      })
      .forEach((task) => {
        const tagIds = task.tagIds ?? [];
        if (tagIds.length === 0) {
          counts[0] = (counts[0] ?? 0) + 1;

          return;
        }

        tagIds.forEach((tagId) => {
          counts[tagId] = (counts[tagId] ?? 0) + 1;
        });
      });

    return counts;
  }, [appContext.globalDataCache.tags, tasks]);

  const tagSystemTypeCounts = useMemo(() => {
    const counts: Partial<Record<TagSystemTypes, number>> = {};
    const systemTypes = [TagSystemTypes.MonitoringFinding, TagSystemTypes.NonConformity, TagSystemTypes.Incident];

    tasks.forEach((task) => {
      const tags = appContext.globalDataCache.tags.getItemsForId(task.tagIds ?? []);
      systemTypes.forEach((systemType) => {
        if (tags.some((tag) => tag.tagSystemType === systemType)) {
          counts[systemType] = (counts[systemType] ?? 0) + 1;
        }
      });
    });

    return counts;
  }, [appContext.globalDataCache.tags, tasks]);

  const totalOpenCount = useMemo(
    () =>
      tasks.filter((task) => task.taskStateId !== TaskStateId.Completed && task.taskStateId !== TaskStateId.Archived)
        .length,
    [tasks],
  );

  if (props.isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  if (!props.selectedProcessingActivity) return null;

  const processingActivityId = props.selectedProcessingActivity.processingActivityId;

  return (
    <IPStack verticalFill tokens={globalStackTokensGapMedium}>
      <IPStack.Item>
        <IPStack horizontal wrap verticalAlign="center" horizontalAlign="space-between">
          <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapMedium}>
            <IPStack.Item>
              <IPText block variant="large" styles={globalTextStylesBold}>
                {`${props.selectedProcessingActivity.code} ${props.selectedProcessingActivity.name}`}
              </IPText>
              <IPText>{t('processingActivity:Dashboard.SubTitle')}</IPText>
            </IPStack.Item>
            {!isLoadingTasks && (
              <IPStack.Item>
                <PrevNextButtons
                  onPrevClick={() => navigateToProcessingActivity(-1)}
                  onNextClick={() => navigateToProcessingActivity(1)}
                  height={globalSceneBarItemHeight}
                  prevTooltip={t('translation:General.Button.Prev')}
                  nextTooltip={t('translation:General.Button.Next')}
                  prevDisabled={isLoadingProcessingActivities || selectedProcessingActivityIndex <= 0}
                  nextDisabled={
                    isLoadingProcessingActivities ||
                    selectedProcessingActivityIndex < 0 ||
                    selectedProcessingActivityIndex >= processingActivities.length - 1
                  }
                />
              </IPStack.Item>
            )}
          </IPStack>
          <IPStack.Item>
            <PeriodFilter
              onUpdatePeriodOption={setPeriod}
              storageKey={LocalStorageKeys.TaskPeriodSelector}
              showAll={true}
            />
          </IPStack.Item>
        </IPStack>
      </IPStack.Item>
      <IPSeparator />
      <IPStack.Item>
        <TaskEffectivenessOverview
          title={t('processingActivity:Dashboard.TaskOverview')}
          info={t('processingActivity:Dashboard.TaskOverviewInfo')}
          totalOpenCount={totalOpenCount}
          openTasksLink={`/processingactivity/${processingActivityId}/tasks?view=list&open`}
          tagSystemTypeCount={tagSystemTypeCounts}
          isUpdating={isLoadingTasks}
          labels={{ deviations: t('processingActivity:Dashboard.AuditDeviations') }}
          onCategoryClick={(tagSystemType) =>
            history.push(`/processingactivity/${processingActivityId}/tasks?view=list&tagCategories=${tagSystemType}`)
          }
        >
          <TaskTagOverview
            tagCounts={tagCounts}
            isUpdating={isLoadingTasks}
            getTaskLinkForTag={(tagId) =>
              tagId ? `/processingactivity/${processingActivityId}/tasks?view=list&tags=${tagId}` : undefined
            }
            showSeparator
          />
        </TaskEffectivenessOverview>
      </IPStack.Item>
      <IPSeparator />
      <ScrollableStackItem minHeight={300}>
        <ProcessingActivityReportContent report={report} reportContext={reportContext} isLoading={isLoadingReport} />
      </ScrollableStackItem>
    </IPStack>
  );
};
